import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  cuentas, 
  colaboradores, 
  herramientas,
  clientesRentabilidad,
  ingresosCliente,
  asignacionesColaboradoresCliente,
  usoHerramientasCliente,
  financiamientosCliente,
  rentabilidadV2 
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { exportRentabilidadHausToPDF } from '../lib/pdfExport';
import { 
  LayoutDashboard,
  Users,
  Wrench,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Plus,
  Trash2,
  Loader2,
  Save,
  Percent,
  Wallet,
  Eye,
  UserCheck,
  FileDown,
  Edit2,
  Building2
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

// ============== UTILS ==============
const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
};

const COLORS = ['#BE123C', '#10B981', '#F59E0B', '#8B5CF6', '#06B6D4', '#EC4899', '#3B82F6', '#84CC16'];

const TIPOS_INGRESO = [
  { value: 'iguala_mensual', label: 'Iguala Mensual' },
  { value: 'proyecto_unico', label: 'Proyecto Único' },
  { value: 'servicio_adicional', label: 'Servicio Adicional' },
  { value: 'ingreso_extraordinario', label: 'Ingreso Extraordinario' },
];

const ESTATUS_OPTIONS = [
  { value: 'activo', label: 'Activo' },
  { value: 'inactivo', label: 'Inactivo' },
  { value: 'pausado', label: 'Pausado' },
];

// ============== MAIN COMPONENT ==============
export default function RentabilidadUnificado() {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('consolidado');
  
  // Data states
  const [resumen, setResumen] = useState(null);
  const [cuentasList, setCuentasList] = useState([]);
  const [colaboradoresList, setColaboradoresList] = useState([]);
  const [herramientasList, setHerramientasList] = useState([]);
  const [clientesRentList, setClientesRentList] = useState([]);
  const [rentabilidadClientes, setRentabilidadClientes] = useState([]);
  const [rentabilidadColaboradores, setRentabilidadColaboradores] = useState([]);
  const [clienteDetalle, setClienteDetalle] = useState(null);
  
  // Filters
  const [selectedCuenta, setSelectedCuenta] = useState('all');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [detalleDialogOpen, setDetalleDialogOpen] = useState(false);
  
  // Form states
  const [clienteRentForm, setClienteRentForm] = useState({ nombre_cliente: '', cuenta_id: '', fecha_inicio: '', estatus: 'activo' });
  const [ingresoForm, setIngresoForm] = useState({ cliente_rentabilidad_id: '', tipo_ingreso: 'iguala_mensual', monto: '', fecha: '', descripcion: '' });
  const [asignacionForm, setAsignacionForm] = useState({ cliente_rentabilidad_id: '', colaborador_id: '', porcentaje_participacion: '' });
  const [usoForm, setUsoForm] = useState({ cliente_rentabilidad_id: '', herramienta_id: '', porcentaje_uso: '' });
  const [financiamientoForm, setFinanciamientoForm] = useState({
    cliente_rentabilidad_id: '', tipo_financiamiento: '', monto_financiado: '', tasa_interes: '',
    pago_mensual: '', fecha_inicio: '', fecha_fin: '', activo: true
  });
  const [colaboradorForm, setColaboradorForm] = useState({ nombre: '', cargo: '', salario_mensual: '', activo: true });
  const [herramientaForm, setHerramientaForm] = useState({ nombre: '', costo_mensual: '', activo: true });

  // ============== DATA LOADING ==============
  useEffect(() => {
    loadAllData();
  }, []);

  useEffect(() => {
    if (!loading) {
      loadResumen();
      loadRentabilidadClientes();
      loadRentabilidadColaboradores();
    }
  }, [selectedCuenta, fechaInicio, fechaFin]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [cuentasRes, colaboradoresRes, herramientasRes, clientesRes] = await Promise.all([
        cuentas.getAll(),
        colaboradores.getAll(),
        herramientas.getAll(),
        clientesRentabilidad.getAll()
      ]);
      
      setCuentasList(cuentasRes.data);
      setColaboradoresList(colaboradoresRes.data);
      setHerramientasList(herramientasRes.data);
      setClientesRentList(clientesRes.data);
      
      await Promise.all([
        loadResumen(),
        loadRentabilidadClientes(),
        loadRentabilidadColaboradores()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const loadResumen = async () => {
    try {
      const params = {};
      if (selectedCuenta !== 'all') params.cuenta_id = selectedCuenta;
      if (fechaInicio) params.fecha_inicio = fechaInicio;
      if (fechaFin) params.fecha_fin = fechaFin;
      
      const res = await rentabilidadV2.getResumen(params);
      setResumen(res.data);
    } catch (error) {
      console.error('Error loading resumen:', error);
    }
  };

  const loadRentabilidadClientes = async () => {
    try {
      const params = {};
      if (selectedCuenta !== 'all') params.cuenta_id = selectedCuenta;
      if (fechaInicio) params.fecha_inicio = fechaInicio;
      if (fechaFin) params.fecha_fin = fechaFin;
      
      const res = await rentabilidadV2.getClientes(params);
      setRentabilidadClientes(res.data);
    } catch (error) {
      console.error('Error loading rentabilidad clientes:', error);
    }
  };

  const loadRentabilidadColaboradores = async () => {
    try {
      const params = {};
      if (selectedCuenta !== 'all') params.cuenta_id = selectedCuenta;
      
      const res = await rentabilidadV2.getColaboradores(params);
      setRentabilidadColaboradores(res.data);
    } catch (error) {
      console.error('Error loading rentabilidad colaboradores:', error);
    }
  };

  const loadClienteDetalle = async (clienteRentId) => {
    try {
      const params = {};
      if (fechaInicio) params.fecha_inicio = fechaInicio;
      if (fechaFin) params.fecha_fin = fechaFin;
      
      const res = await rentabilidadV2.getClienteDetalle(clienteRentId, params);
      setClienteDetalle(res.data);
      setDetalleDialogOpen(true);
    } catch (error) {
      console.error('Error loading cliente detalle:', error);
      toast.error('Error al cargar el detalle del cliente');
    }
  };

  // ============== DIALOG HANDLERS ==============
  const openDialog = (type, item = null) => {
    setDialogType(type);
    setEditingId(item?.id || null);
    
    const today = new Date().toISOString().split('T')[0];
    
    if (item) {
      switch (type) {
        case 'colaborador':
          setColaboradorForm({ 
            nombre: item.nombre, 
            cargo: item.cargo, 
            salario_mensual: item.salario_mensual?.toString() || '', 
            activo: item.activo ?? true 
          });
          break;
        case 'herramienta':
          setHerramientaForm({ 
            nombre: item.nombre, 
            costo_mensual: item.costo_mensual?.toString() || '', 
            activo: item.activo ?? true 
          });
          break;
        default:
          break;
      }
    } else {
      // Reset forms for new items
      setClienteRentForm({ nombre_cliente: '', cuenta_id: cuentasList[0]?.id || '', fecha_inicio: today, estatus: 'activo' });
      setIngresoForm({ cliente_rentabilidad_id: clientesRentList[0]?.id || '', tipo_ingreso: 'iguala_mensual', monto: '', fecha: today, descripcion: '' });
      setAsignacionForm({ cliente_rentabilidad_id: clientesRentList[0]?.id || '', colaborador_id: colaboradoresList[0]?.id || '', porcentaje_participacion: '' });
      setUsoForm({ cliente_rentabilidad_id: clientesRentList[0]?.id || '', herramienta_id: herramientasList[0]?.id || '', porcentaje_uso: '' });
      setFinanciamientoForm({ cliente_rentabilidad_id: clientesRentList[0]?.id || '', tipo_financiamiento: '', monto_financiado: '', tasa_interes: '', pago_mensual: '', fecha_inicio: '', fecha_fin: '', activo: true });
      setColaboradorForm({ nombre: '', cargo: '', salario_mensual: '', activo: true });
      setHerramientaForm({ nombre: '', costo_mensual: '', activo: true });
    }
    
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      switch (dialogType) {
        case 'cliente':
          await clientesRentabilidad.create(clienteRentForm);
          toast.success('Cliente creado exitosamente');
          break;
        case 'ingreso':
          await ingresosCliente.create({ ...ingresoForm, monto: parseFloat(ingresoForm.monto) });
          toast.success('Ingreso registrado exitosamente');
          break;
        case 'asignacion':
          await asignacionesColaboradoresCliente.create({ ...asignacionForm, porcentaje_participacion: parseFloat(asignacionForm.porcentaje_participacion) });
          toast.success('Asignación creada exitosamente');
          break;
        case 'uso_herramienta':
          await usoHerramientasCliente.create({ ...usoForm, porcentaje_uso: parseFloat(usoForm.porcentaje_uso) });
          toast.success('Uso de herramienta registrado');
          break;
        case 'financiamiento':
          await financiamientosCliente.create({
            ...financiamientoForm,
            monto_financiado: parseFloat(financiamientoForm.monto_financiado),
            tasa_interes: parseFloat(financiamientoForm.tasa_interes),
            pago_mensual: parseFloat(financiamientoForm.pago_mensual)
          });
          toast.success('Financiamiento creado exitosamente');
          break;
        case 'colaborador':
          if (editingId) {
            await colaboradores.update(editingId, { ...colaboradorForm, salario_mensual: parseFloat(colaboradorForm.salario_mensual) });
            toast.success('Colaborador actualizado');
          } else {
            await colaboradores.create({ ...colaboradorForm, salario_mensual: parseFloat(colaboradorForm.salario_mensual) });
            toast.success('Colaborador creado exitosamente');
          }
          break;
        case 'herramienta':
          if (editingId) {
            await herramientas.update(editingId, { ...herramientaForm, costo_mensual: parseFloat(herramientaForm.costo_mensual) });
            toast.success('Herramienta actualizada');
          } else {
            await herramientas.create({ ...herramientaForm, costo_mensual: parseFloat(herramientaForm.costo_mensual) });
            toast.success('Herramienta creada exitosamente');
          }
          break;
        default:
          break;
      }
      setDialogOpen(false);
      setEditingId(null);
      loadAllData();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Error al guardar: ' + (error.response?.data?.detail || 'Error desconocido'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('¿Eliminar este registro?')) return;
    try {
      switch (type) {
        case 'cliente': await clientesRentabilidad.delete(id); break;
        case 'colaborador': await colaboradores.delete(id); break;
        case 'herramienta': await herramientas.delete(id); break;
        default: break;
      }
      toast.success('Registro eliminado');
      loadAllData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // ============== COMPUTED DATA ==============
  const costosData = useMemo(() => {
    if (!resumen) return [];
    return [
      { 
        name: 'Personal', 
        value: resumen.costo_personal, 
        color: COLORS[0],
        detalle: rentabilidadColaboradores.slice(0, 5).map(c => `${c.nombre}: ${formatCurrency(c.salario_mensual)}`).join('\n')
      },
      { 
        name: 'Herramientas', 
        value: resumen.costo_herramientas, 
        color: COLORS[1],
        detalle: herramientasList.filter(h => h.activo).slice(0, 5).map(h => `${h.nombre}: ${formatCurrency(h.costo_mensual)}`).join('\n')
      },
      { 
        name: 'Financiamiento', 
        value: resumen.costo_financiamiento, 
        color: COLORS[2],
        detalle: 'Pagos de financiamientos activos'
      }
    ].filter(item => item.value > 0);
  }, [resumen, rentabilidadColaboradores, herramientasList]);

  // Custom tooltip for costs chart
  const CostosTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg max-w-xs">
          <p className="font-bold text-sm" style={{ color: data.color }}>{data.name}</p>
          <p className="text-lg font-bold">{formatCurrency(data.value)}</p>
          {data.detalle && (
            <div className="mt-2 pt-2 border-t text-xs text-gray-600 whitespace-pre-line">
              {data.detalle}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const topClientes = useMemo(() => {
    return [...rentabilidadClientes]
      .sort((a, b) => b.utilidad - a.utilidad)
      .slice(0, 5);
  }, [rentabilidadClientes]);

  // ============== RENDER ==============
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="rentabilidad-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Rentabilidad
          </h1>
          <p className="text-gray-500 text-sm">Análisis de rentabilidad por cliente, cuenta y colaborador</p>
        </div>
        
        {isAdmin && (
          <Button 
            variant="default" 
            size="sm" 
            onClick={() => {
              exportRentabilidadHausToPDF(resumen, rentabilidadClientes, [], rentabilidadColaboradores);
              toast.success('PDF generado exitosamente');
            }}
            data-testid="export-pdf-btn"
          >
            <FileDown className="w-4 h-4 mr-1" />
            Exportar PDF
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs">Cuenta</Label>
              <Select value={selectedCuenta} onValueChange={setSelectedCuenta}>
                <SelectTrigger data-testid="filter-cuenta">
                  <SelectValue placeholder="Todas las cuentas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las cuentas</SelectItem>
                  {cuentasList.map(cuenta => (
                    <SelectItem key={cuenta.id} value={cuenta.id}>{cuenta.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Fecha Inicio</Label>
              <Input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} data-testid="filter-fecha-inicio" />
            </div>
            <div>
              <Label className="text-xs">Fecha Fin</Label>
              <Input type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} data-testid="filter-fecha-fin" />
            </div>
            <div className="flex items-end">
              <Button variant="outline" size="sm" onClick={() => { setSelectedCuenta('all'); setFechaInicio(''); setFechaFin(''); }}>
                Limpiar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex w-full overflow-x-auto gap-1 p-1">
          <TabsTrigger value="consolidado" data-testid="tab-consolidado" className="text-[10px] sm:text-sm px-2 sm:px-4 whitespace-nowrap">
            <LayoutDashboard className="w-3 h-3 sm:w-4 sm:h-4 mr-1 hidden sm:inline" />
            Consolidado
          </TabsTrigger>
          <TabsTrigger value="clientes" data-testid="tab-clientes" className="text-[10px] sm:text-sm px-2 sm:px-4 whitespace-nowrap">
            <Building2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 hidden sm:inline" />
            Clientes
          </TabsTrigger>
          <TabsTrigger value="colaboradores" data-testid="tab-colaboradores" className="text-[10px] sm:text-sm px-2 sm:px-4 whitespace-nowrap">
            <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 hidden sm:inline" />
            Colab.
          </TabsTrigger>
          <TabsTrigger value="herramientas" data-testid="tab-herramientas" className="text-[10px] sm:text-sm px-2 sm:px-4 whitespace-nowrap">
            <Wrench className="w-3 h-3 sm:w-4 sm:h-4 mr-1 hidden sm:inline" />
            Herram.
          </TabsTrigger>
          <TabsTrigger value="financiamiento" data-testid="tab-financiamiento" className="text-[10px] sm:text-sm px-2 sm:px-4 whitespace-nowrap">
            <Wallet className="w-3 h-3 sm:w-4 sm:h-4 mr-1 hidden sm:inline" />
            Financ.
          </TabsTrigger>
        </TabsList>

        {/* ============== TAB: CONSOLIDADO ============== */}
        <TabsContent value="consolidado" className="space-y-6">
          {/* KPIs Row 1 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card>
              <CardContent className="p-3 md:p-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-green-600 flex-shrink-0" />
                  <span className="text-xs text-gray-500 truncate">Ingresos</span>
                </div>
                <div className="text-lg md:text-2xl font-bold text-green-600 mt-1 truncate">
                  {formatCurrency(resumen?.total_ingresos)}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 md:p-4">
                <div className="flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 md:w-5 md:h-5 text-red-600 flex-shrink-0" />
                  <span className="text-xs text-gray-500 truncate">Costos</span>
                </div>
                <div className="text-lg md:text-2xl font-bold text-red-600 mt-1 truncate">
                  {formatCurrency(resumen?.total_costos)}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 md:p-4">
                <div className="flex items-center gap-2">
                  <Wallet className="w-4 h-4 md:w-5 md:h-5 text-blue-600 flex-shrink-0" />
                  <span className="text-xs text-gray-500 truncate">Utilidad</span>
                </div>
                <div className={`text-lg md:text-2xl font-bold mt-1 truncate ${(resumen?.utilidad_total || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(resumen?.utilidad_total)}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 md:p-4">
                <div className="flex items-center gap-2">
                  <Percent className="w-4 h-4 md:w-5 md:h-5 text-purple-600 flex-shrink-0" />
                  <span className="text-xs text-gray-500 truncate">Margen</span>
                </div>
                <div className={`text-lg md:text-2xl font-bold mt-1 ${(resumen?.margen_promedio || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {resumen?.margen_promedio || 0}%
                </div>
              </CardContent>
            </Card>
          </div>

          {/* KPIs Row 2 - Desglose */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="bg-gray-50">
              <CardContent className="p-3">
                <div className="text-xs text-gray-500">Total Clientes</div>
                <div className="text-lg font-bold">{resumen?.total_clientes || 0}</div>
              </CardContent>
            </Card>
            <Card className="bg-green-50">
              <CardContent className="p-3">
                <div className="text-xs text-gray-500">Clientes Rentables</div>
                <div className="text-lg font-bold text-green-600">{resumen?.clientes_rentables || 0}</div>
              </CardContent>
            </Card>
            <Card className="bg-red-50">
              <CardContent className="p-3">
                <div className="text-xs text-gray-500">No Rentables</div>
                <div className="text-lg font-bold text-red-600">{resumen?.clientes_no_rentables || 0}</div>
              </CardContent>
            </Card>
            <Card className="bg-blue-50">
              <CardContent className="p-3">
                <div className="text-xs text-gray-500">Costo Personal</div>
                <div className="text-lg font-bold">{formatCurrency(resumen?.costo_personal)}</div>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Desglose de Costos */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Desglose de Costos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  {costosData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={costosData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {costosData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<CostosTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400">
                      Sin datos de costos
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Top 5 Clientes */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Top 5 Clientes por Utilidad</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  {topClientes.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={topClientes} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <YAxis type="category" dataKey="nombre_cliente" width={100} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Bar dataKey="utilidad" fill="#10B981" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400">
                      Sin datos de clientes
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ============== TAB: CLIENTES ============== */}
        <TabsContent value="clientes" className="space-y-4">
          {isAdmin && (
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" onClick={() => openDialog('cliente')} data-testid="add-cliente-btn">
                <Plus className="w-4 h-4 mr-1" /> Nuevo Cliente
              </Button>
              <Button variant="outline" size="sm" onClick={() => openDialog('ingreso')} data-testid="add-ingreso-btn">
                <DollarSign className="w-4 h-4 mr-1" /> Registrar Ingreso
              </Button>
            </div>
          )}

          {/* Tabla de Clientes */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-3 font-medium">Cliente</th>
                      <th className="text-left p-3 font-medium hidden md:table-cell">Cuenta</th>
                      <th className="text-right p-3 font-medium">Ingresos</th>
                      <th className="text-right p-3 font-medium">Costos</th>
                      <th className="text-right p-3 font-medium">Utilidad</th>
                      <th className="text-center p-3 font-medium">Margen</th>
                      <th className="p-3 w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rentabilidadClientes.map((cliente, idx) => (
                      <tr key={cliente.cliente_rentabilidad_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="p-3">
                          <div className="font-medium">{cliente.nombre_cliente}</div>
                          <div className="text-xs text-gray-500 md:hidden">{cliente.cuenta_nombre}</div>
                        </td>
                        <td className="p-3 hidden md:table-cell text-gray-500">{cliente.cuenta_nombre}</td>
                        <td className="p-3 text-right text-green-600">{formatCurrency(cliente.ingreso_total)}</td>
                        <td className="p-3 text-right text-red-600">{formatCurrency(cliente.costo_total)}</td>
                        <td className={`p-3 text-right font-medium ${cliente.utilidad >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(cliente.utilidad)}
                        </td>
                        <td className="p-3 text-center">
                          <Badge variant={cliente.margen >= 20 ? 'default' : cliente.margen >= 0 ? 'secondary' : 'destructive'}>
                            {cliente.margen}%
                          </Badge>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-1 justify-end">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => loadClienteDetalle(cliente.cliente_rentabilidad_id)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            {isAdmin && (
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete('cliente', cliente.cliente_rentabilidad_id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {rentabilidadClientes.length === 0 && (
                      <tr>
                        <td colSpan={7} className="p-8 text-center text-gray-400">
                          No hay clientes registrados
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============== TAB: COLABORADORES ============== */}
        <TabsContent value="colaboradores" className="space-y-4">
          {isAdmin && (
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" onClick={() => openDialog('colaborador')} data-testid="add-colaborador-btn">
                <Plus className="w-4 h-4 mr-1" /> Nuevo Colaborador
              </Button>
              <Button variant="outline" size="sm" onClick={() => openDialog('asignacion')} data-testid="add-asignacion-btn">
                <UserCheck className="w-4 h-4 mr-1" /> Asignar a Cliente
              </Button>
            </div>
          )}

          {/* Lista de Colaboradores */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Colaboradores Activos</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-3 font-medium">Nombre</th>
                      <th className="text-left p-3 font-medium hidden md:table-cell">Cargo</th>
                      <th className="text-right p-3 font-medium">Salario</th>
                      <th className="text-center p-3 font-medium">Carga</th>
                      <th className="text-right p-3 font-medium hidden md:table-cell">Costo Imputado</th>
                      <th className="text-right p-3 font-medium">Rentabilidad</th>
                      {isAdmin && <th className="p-3 w-20"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rentabilidadColaboradores.map((colab, idx) => (
                      <tr key={colab.colaborador_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="p-3">
                          <div className="font-medium">{colab.nombre}</div>
                          <div className="text-xs text-gray-500 md:hidden">{colab.cargo}</div>
                        </td>
                        <td className="p-3 hidden md:table-cell text-gray-500">{colab.cargo}</td>
                        <td className="p-3 text-right">{formatCurrency(colab.salario_mensual)}</td>
                        <td className="p-3 text-center">
                          <Badge variant={colab.porcentaje_carga > 100 ? 'destructive' : 'outline'}>
                            {colab.porcentaje_carga}%
                          </Badge>
                        </td>
                        <td className="p-3 text-right text-red-600 hidden md:table-cell">{formatCurrency(colab.costo_imputado)}</td>
                        <td className={`p-3 text-right font-medium ${colab.rentabilidad_generada >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(colab.rentabilidad_generada)}
                        </td>
                        {isAdmin && (
                          <td className="p-3">
                            <div className="flex gap-1 justify-end">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => {
                                const fullColab = colaboradoresList.find(c => c.id === colab.colaborador_id);
                                if (fullColab) openDialog('colaborador', fullColab);
                              }}>
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete('colaborador', colab.colaborador_id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                    {rentabilidadColaboradores.length === 0 && (
                      <tr>
                        <td colSpan={isAdmin ? 7 : 6} className="p-8 text-center text-gray-400">
                          No hay colaboradores registrados
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============== TAB: HERRAMIENTAS ============== */}
        <TabsContent value="herramientas" className="space-y-4">
          {isAdmin && (
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" onClick={() => openDialog('herramienta')} data-testid="add-herramienta-btn">
                <Plus className="w-4 h-4 mr-1" /> Nueva Herramienta
              </Button>
              <Button variant="outline" size="sm" onClick={() => openDialog('uso_herramienta')} data-testid="add-uso-btn">
                <Wrench className="w-4 h-4 mr-1" /> Asignar a Cliente
              </Button>
            </div>
          )}

          {/* Lista de Herramientas */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Herramientas y Software</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-3 font-medium">Nombre</th>
                      <th className="text-right p-3 font-medium">Costo Mensual</th>
                      <th className="text-center p-3 font-medium">Estado</th>
                      {isAdmin && <th className="p-3 w-20"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {herramientasList.map((herr, idx) => (
                      <tr key={herr.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="p-3 font-medium">{herr.nombre}</td>
                        <td className="p-3 text-right">{formatCurrency(herr.costo_mensual)}</td>
                        <td className="p-3 text-center">
                          <Badge variant={herr.activo ? 'default' : 'secondary'}>
                            {herr.activo ? 'Activa' : 'Inactiva'}
                          </Badge>
                        </td>
                        {isAdmin && (
                          <td className="p-3">
                            <div className="flex gap-1 justify-end">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDialog('herramienta', herr)}>
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete('herramienta', herr.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                    {herramientasList.length === 0 && (
                      <tr>
                        <td colSpan={isAdmin ? 4 : 3} className="p-8 text-center text-gray-400">
                          No hay herramientas registradas
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Resumen de Costos */}
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-amber-700">Costo Total Herramientas</div>
                  <div className="text-2xl font-bold text-amber-800">
                    {formatCurrency(herramientasList.filter(h => h.activo).reduce((sum, h) => sum + (h.costo_mensual || 0), 0))}
                  </div>
                </div>
                <Wrench className="w-8 h-8 text-amber-600" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============== TAB: FINANCIAMIENTO ============== */}
        <TabsContent value="financiamiento" className="space-y-4">
          {isAdmin && (
            <Button size="sm" onClick={() => openDialog('financiamiento')} data-testid="add-financiamiento-btn">
              <Plus className="w-4 h-4 mr-1" /> Nuevo Financiamiento
            </Button>
          )}

          {/* Info Card */}
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-purple-700">Costo Total Financiamiento</div>
                  <div className="text-2xl font-bold text-purple-800">
                    {formatCurrency(resumen?.costo_financiamiento)}
                  </div>
                  <div className="text-xs text-purple-600 mt-1">Pagos mensuales activos</div>
                </div>
                <Wallet className="w-8 h-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          {/* Instructions */}
          <Card>
            <CardContent className="p-6 text-center text-gray-500">
              <Wallet className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">
                Los financiamientos se asignan directamente a cada cliente desde el formulario "Nuevo Financiamiento".
              </p>
              <p className="text-xs mt-2">
                Para ver los financiamientos de un cliente específico, usa el botón <Eye className="w-3 h-3 inline" /> en la pestaña de Clientes.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ============== DIALOGS ============== */}
      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditingId(null); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {dialogType === 'cliente' && 'Nuevo Cliente'}
              {dialogType === 'ingreso' && 'Registrar Ingreso'}
              {dialogType === 'asignacion' && 'Asignar Colaborador a Cliente'}
              {dialogType === 'uso_herramienta' && 'Asignar Herramienta a Cliente'}
              {dialogType === 'financiamiento' && 'Nuevo Financiamiento'}
              {dialogType === 'colaborador' && (editingId ? 'Editar Colaborador' : 'Nuevo Colaborador')}
              {dialogType === 'herramienta' && (editingId ? 'Editar Herramienta' : 'Nueva Herramienta')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Cliente Form */}
            {dialogType === 'cliente' && (
              <>
                <div>
                  <Label>Nombre del Cliente *</Label>
                  <Input 
                    value={clienteRentForm.nombre_cliente} 
                    onChange={(e) => setClienteRentForm({...clienteRentForm, nombre_cliente: e.target.value})}
                    placeholder="Ej: Cliente ABC"
                    data-testid="input-nombre-cliente"
                  />
                </div>
                <div>
                  <Label>Cuenta *</Label>
                  <Select value={clienteRentForm.cuenta_id} onValueChange={(v) => setClienteRentForm({...clienteRentForm, cuenta_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar cuenta" /></SelectTrigger>
                    <SelectContent>
                      {cuentasList.map(cuenta => (
                        <SelectItem key={cuenta.id} value={cuenta.id}>{cuenta.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fecha Inicio</Label>
                  <Input type="date" value={clienteRentForm.fecha_inicio} onChange={(e) => setClienteRentForm({...clienteRentForm, fecha_inicio: e.target.value})} />
                </div>
                <div>
                  <Label>Estatus</Label>
                  <Select value={clienteRentForm.estatus} onValueChange={(v) => setClienteRentForm({...clienteRentForm, estatus: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ESTATUS_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Ingreso Form */}
            {dialogType === 'ingreso' && (
              <>
                <div>
                  <Label>Cliente *</Label>
                  <Select value={ingresoForm.cliente_rentabilidad_id} onValueChange={(v) => setIngresoForm({...ingresoForm, cliente_rentabilidad_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar cliente" /></SelectTrigger>
                    <SelectContent>
                      {clientesRentList.map(cliente => (
                        <SelectItem key={cliente.id} value={cliente.id}>{cliente.nombre_cliente} ({cliente.cuenta_nombre})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tipo de Ingreso *</Label>
                  <Select value={ingresoForm.tipo_ingreso} onValueChange={(v) => setIngresoForm({...ingresoForm, tipo_ingreso: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIPOS_INGRESO.map(tipo => (
                        <SelectItem key={tipo.value} value={tipo.value}>{tipo.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Monto *</Label>
                    <Input type="number" value={ingresoForm.monto} onChange={(e) => setIngresoForm({...ingresoForm, monto: e.target.value})} placeholder="$0.00" />
                  </div>
                  <div>
                    <Label>Fecha *</Label>
                    <Input type="date" value={ingresoForm.fecha} onChange={(e) => setIngresoForm({...ingresoForm, fecha: e.target.value})} />
                  </div>
                </div>
                <div>
                  <Label>Descripción</Label>
                  <Input value={ingresoForm.descripcion} onChange={(e) => setIngresoForm({...ingresoForm, descripcion: e.target.value})} />
                </div>
              </>
            )}

            {/* Asignacion Form */}
            {dialogType === 'asignacion' && (
              <>
                <div>
                  <Label>Cliente *</Label>
                  <Select value={asignacionForm.cliente_rentabilidad_id} onValueChange={(v) => setAsignacionForm({...asignacionForm, cliente_rentabilidad_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar cliente" /></SelectTrigger>
                    <SelectContent>
                      {clientesRentList.map(cliente => (
                        <SelectItem key={cliente.id} value={cliente.id}>{cliente.nombre_cliente}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Colaborador *</Label>
                  <Select value={asignacionForm.colaborador_id} onValueChange={(v) => setAsignacionForm({...asignacionForm, colaborador_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar colaborador" /></SelectTrigger>
                    <SelectContent>
                      {colaboradoresList.filter(c => c.activo).map(colab => (
                        <SelectItem key={colab.id} value={colab.id}>{colab.nombre} - {colab.cargo}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>% Participación *</Label>
                  <Input type="number" min="0" max="100" value={asignacionForm.porcentaje_participacion} onChange={(e) => setAsignacionForm({...asignacionForm, porcentaje_participacion: e.target.value})} placeholder="Ej: 40" />
                </div>
              </>
            )}

            {/* Uso Herramienta Form */}
            {dialogType === 'uso_herramienta' && (
              <>
                <div>
                  <Label>Cliente *</Label>
                  <Select value={usoForm.cliente_rentabilidad_id} onValueChange={(v) => setUsoForm({...usoForm, cliente_rentabilidad_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar cliente" /></SelectTrigger>
                    <SelectContent>
                      {clientesRentList.map(cliente => (
                        <SelectItem key={cliente.id} value={cliente.id}>{cliente.nombre_cliente}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Herramienta *</Label>
                  <Select value={usoForm.herramienta_id} onValueChange={(v) => setUsoForm({...usoForm, herramienta_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar herramienta" /></SelectTrigger>
                    <SelectContent>
                      {herramientasList.filter(h => h.activo).map(herr => (
                        <SelectItem key={herr.id} value={herr.id}>{herr.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>% Uso *</Label>
                  <Input type="number" min="0" max="100" value={usoForm.porcentaje_uso} onChange={(e) => setUsoForm({...usoForm, porcentaje_uso: e.target.value})} placeholder="Ej: 25" />
                </div>
              </>
            )}

            {/* Financiamiento Form */}
            {dialogType === 'financiamiento' && (
              <>
                <div>
                  <Label>Cliente *</Label>
                  <Select value={financiamientoForm.cliente_rentabilidad_id} onValueChange={(v) => setFinanciamientoForm({...financiamientoForm, cliente_rentabilidad_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar cliente" /></SelectTrigger>
                    <SelectContent>
                      {clientesRentList.map(cliente => (
                        <SelectItem key={cliente.id} value={cliente.id}>{cliente.nombre_cliente}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tipo de Financiamiento *</Label>
                  <Input value={financiamientoForm.tipo_financiamiento} onChange={(e) => setFinanciamientoForm({...financiamientoForm, tipo_financiamiento: e.target.value})} placeholder="Ej: Crédito bancario" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Monto Financiado *</Label>
                    <Input type="number" value={financiamientoForm.monto_financiado} onChange={(e) => setFinanciamientoForm({...financiamientoForm, monto_financiado: e.target.value})} />
                  </div>
                  <div>
                    <Label>Tasa Interés % *</Label>
                    <Input type="number" step="0.1" value={financiamientoForm.tasa_interes} onChange={(e) => setFinanciamientoForm({...financiamientoForm, tasa_interes: e.target.value})} />
                  </div>
                </div>
                <div>
                  <Label>Pago Mensual *</Label>
                  <Input type="number" value={financiamientoForm.pago_mensual} onChange={(e) => setFinanciamientoForm({...financiamientoForm, pago_mensual: e.target.value})} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Fecha Inicio *</Label>
                    <Input type="date" value={financiamientoForm.fecha_inicio} onChange={(e) => setFinanciamientoForm({...financiamientoForm, fecha_inicio: e.target.value})} />
                  </div>
                  <div>
                    <Label>Fecha Fin *</Label>
                    <Input type="date" value={financiamientoForm.fecha_fin} onChange={(e) => setFinanciamientoForm({...financiamientoForm, fecha_fin: e.target.value})} />
                  </div>
                </div>
              </>
            )}

            {/* Colaborador Form */}
            {dialogType === 'colaborador' && (
              <>
                <div>
                  <Label>Nombre *</Label>
                  <Input value={colaboradorForm.nombre} onChange={(e) => setColaboradorForm({...colaboradorForm, nombre: e.target.value})} placeholder="Nombre completo" />
                </div>
                <div>
                  <Label>Cargo *</Label>
                  <Input value={colaboradorForm.cargo} onChange={(e) => setColaboradorForm({...colaboradorForm, cargo: e.target.value})} placeholder="Ej: Analista, Director" />
                </div>
                <div>
                  <Label>Salario Mensual *</Label>
                  <Input type="number" value={colaboradorForm.salario_mensual} onChange={(e) => setColaboradorForm({...colaboradorForm, salario_mensual: e.target.value})} placeholder="$0.00" />
                </div>
              </>
            )}

            {/* Herramienta Form */}
            {dialogType === 'herramienta' && (
              <>
                <div>
                  <Label>Nombre *</Label>
                  <Input value={herramientaForm.nombre} onChange={(e) => setHerramientaForm({...herramientaForm, nombre: e.target.value})} placeholder="Ej: Power BI, Figma" />
                </div>
                <div>
                  <Label>Costo Mensual *</Label>
                  <Input type="number" value={herramientaForm.costo_mensual} onChange={(e) => setHerramientaForm({...herramientaForm, costo_mensual: e.target.value})} placeholder="$0.00" />
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Save className="w-4 h-4 mr-2" />
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detalle Cliente Dialog */}
      <Dialog open={detalleDialogOpen} onOpenChange={setDetalleDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle: {clienteDetalle?.nombre_cliente}</DialogTitle>
          </DialogHeader>

          {clienteDetalle && (
            <div className="space-y-6 py-4">
              {/* Basic info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Cuenta</Label>
                  <p className="font-medium">{clienteDetalle.cuenta_nombre}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Estatus</Label>
                  <Badge variant={clienteDetalle.estatus === 'activo' ? 'default' : 'secondary'}>
                    {clienteDetalle.estatus}
                  </Badge>
                </div>
              </div>

              {/* Financial summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card>
                  <CardContent className="p-3">
                    <div className="text-xs text-gray-500">Ingreso Total</div>
                    <div className="text-lg font-bold text-green-600">{formatCurrency(clienteDetalle.ingreso_total)}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <div className="text-xs text-gray-500">Costo Total</div>
                    <div className="text-lg font-bold text-red-600">{formatCurrency(clienteDetalle.costo_total)}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <div className="text-xs text-gray-500">Utilidad</div>
                    <div className={`text-lg font-bold ${clienteDetalle.utilidad >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(clienteDetalle.utilidad)}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <div className="text-xs text-gray-500">Margen</div>
                    <div className={`text-lg font-bold ${clienteDetalle.margen >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {clienteDetalle.margen}%
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Cost breakdown */}
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-gray-500">Personal</div>
                  <div className="font-medium">{formatCurrency(clienteDetalle.costo_personal)}</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-gray-500">Herramientas</div>
                  <div className="font-medium">{formatCurrency(clienteDetalle.costo_herramientas)}</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-gray-500">Financiamiento</div>
                  <div className="font-medium">{formatCurrency(clienteDetalle.costo_financiamiento)}</div>
                </div>
              </div>

              {/* Collaborators */}
              {clienteDetalle.colaboradores_asignados?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Colaboradores Asignados
                  </h4>
                  <div className="space-y-2">
                    {clienteDetalle.colaboradores_asignados.map((colab, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <div>
                          <span className="font-medium">{colab.nombre}</span>
                          <span className="text-gray-400 ml-2">{colab.cargo}</span>
                        </div>
                        <div className="text-right">
                          <Badge variant="outline" className="mr-2">{colab.porcentaje}%</Badge>
                          <span className="text-red-600">{formatCurrency(colab.costo_imputado)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tools */}
              {clienteDetalle.herramientas_usadas?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Wrench className="w-4 h-4" />
                    Herramientas Usadas
                  </h4>
                  <div className="space-y-2">
                    {clienteDetalle.herramientas_usadas.map((herr, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <span className="font-medium">{herr.nombre}</span>
                        <div className="text-right">
                          <Badge variant="outline" className="mr-2">{herr.porcentaje}%</Badge>
                          <span className="text-red-600">{formatCurrency(herr.costo_imputado)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Income by type */}
              {clienteDetalle.ingresos_por_tipo?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Ingresos por Tipo
                  </h4>
                  <div className="space-y-2">
                    {clienteDetalle.ingresos_por_tipo.map((ing, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <div>
                          <span className="font-medium">{TIPOS_INGRESO.find(t => t.value === ing.tipo)?.label || ing.tipo}</span>
                          <span className="text-gray-400 ml-2">({ing.cantidad} registros)</span>
                        </div>
                        <span className="text-green-600 font-medium">{formatCurrency(ing.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Financiamiento activo */}
              {clienteDetalle.financiamientos_activos?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Wallet className="w-4 h-4" />
                    Financiamientos Activos
                  </h4>
                  <div className="space-y-2">
                    {clienteDetalle.financiamientos_activos.map((fin, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-purple-50 rounded">
                        <div>
                          <span className="font-medium">{fin.tipo}</span>
                          <span className="text-gray-400 ml-2">{formatCurrency(fin.monto_financiado)}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-purple-600 font-medium">{formatCurrency(fin.pago_mensual)}/mes</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
